import React, { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import SettingsForm from '@components/settings/settings-form';
import { getEmailSettingsConfig } from '@constants/config';
import ToggleControl from '@shared/form/toggle-control';
import DropdownControl from '@shared/form/dropdown-control';
import '../settings.scss';

const Details = () => {
  const [disabled, setDisabled] = useState(false);
  const history = useHistory();
  const { type } = useParams();

  const data = useSelector(state => {
    const { email } = state.settings;
    return email ? email[type] : null;
  });

  const getLayout = useCallback(() => {
    return getEmailSettingsConfig().details[type];
  }, [type]);

  const layout = getLayout();

  const checkDisabled = useCallback((column, val) => {
    // If it's a toggle column:
    if (column.is_toggle) {
      // Find the display value:
      const value = column.values.find(item => item.id === val).value;
      // If it's Off, disable all other fields:
      if (value === 'Off') {
        setDisabled(true);
      } else {
        setDisabled(false);
      }
    }
  }, []);

  const afterReceiveProps = useCallback(formData => {
    // Check if we must disable the fields after load:
    const currentLayout = getLayout();
    currentLayout.columns.forEach(column => checkDisabled(column, formData[column.field]));
  }, [getLayout, checkDisabled]);

  const onBackClick = useCallback(() => {
    history.push('/settings/email');
  }, [history]);

  const getPayload = useCallback(formData => ({
    email: {
      [type]: { ...formData }
    }
  }), [type]);

  const renderForm = useCallback(({ data: formData, onChange }) => (
    <div styleName="form">
      {layout.columns.map(column => {
        const hintText = column.hint && column.hint[type];
        const hintId = `${column.field}-hint`;
        return (
          <div styleName="row row-single-split" key={column.field}>
            <div styleName="col1">
              <div id={`${column.field}-label`} styleName="row-title">
                {column.label}
              </div>
              <div id={hintId} styleName="row-hint">
                {hintText}
              </div>
            </div>
            <div styleName="col2">
              {column.type === 'dropdown' && (
                <DropdownControl
                  id={column.field}
                  labelId={`${column.field}-label`}
                  describedBy={hintText ? hintId : null}
                  onChange={val => {
                    checkDisabled(column, val);
                    onChange(column.field, val);
                  }}
                  selected={formData[column.field]}
                  values={column.values}
                />
              )}
              {column.type === 'toggle' && (
                <ToggleControl
                  disabled={disabled}
                  legend={column.label}
                  name={column.field}
                  describedBy={hintText ? hintId : null}
                  onChange={val => onChange(column.field, val)}
                  value={formData[column.field]}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  ), [layout, type, disabled, checkDisabled]);

  return (
    <SettingsForm
      data={data}
      layout={layout}
      getPayload={getPayload}
      onBackClick={onBackClick}
      afterReceiveProps={afterReceiveProps}
      renderForm={renderForm}
    />
  );
};

export default Details;
